import { useEffect, useState } from "react";
import { Endpoint } from "../../api/base";

export type DuplicateField = "client_file_no" | "property_address";

export type DuplicateMatch = {
  file_no: string;
  client: string | null;
  subject_name: string | null;
  status: string | null;
  created_at: string | null;
  client_file_no: string | null;
  property_address: string | null;
};

export const DUPLICATE_CHECK_DELAY_MS = 600;
// Keeps "Checking…" from flashing on a fast response
export const MIN_CHECKING_MS = 400;
const MIN_LENGTH = 3;

/**
 * Looks up existing cases with the same Client File # or property street.
 * Failures count as no matches: the warning is advisory and must never block
 * creating a case.
 */
export function useDuplicateCheck(field: DuplicateField, value: string | null | undefined) {
  const [matches, setMatches] = useState<DuplicateMatch[]>([]);
  const [checking, setChecking] = useState(false);
  const trimmed = (value ?? "").trim();

  useEffect(() => {
    setMatches([]);
    setChecking(false);
    if (trimmed.length < MIN_LENGTH) return undefined;

    const controller = new AbortController();
    let settle: ReturnType<typeof setTimeout> | undefined;
    const timer = setTimeout(async () => {
      setChecking(true);
      const started = Date.now();
      let found: DuplicateMatch[] = [];
      try {
        const res = await Endpoint.checkDuplicate(field, trimmed, controller.signal);
        found = Array.isArray(res?.data?.matches) ? res.data.matches : [];
      } catch {
        // Treated as no matches
      }
      if (controller.signal.aborted) return;
      settle = setTimeout(() => {
        setChecking(false);
        setMatches(found);
      }, Math.max(0, MIN_CHECKING_MS - (Date.now() - started)));
    }, DUPLICATE_CHECK_DELAY_MS);

    return () => {
      clearTimeout(timer);
      if (settle) clearTimeout(settle);
      controller.abort();
    };
  }, [field, trimmed]);

  return { matches, checking };
}
